import { UserIcon } from '@phosphor-icons/react'

import { Icon } from '@/components/ui/icon'
import { getImagePreviewTriggerProps, ZoomHint } from '@/components/ui/zoomable-image'
import { useImageFallback } from '@/hooks/use-image-fallback'

import * as S from './avatar.styled'
import { AvatarProps, AvatarSizeKeys } from './avatar.types'

const ZOOM_HINT_SIZES: Record<AvatarSizeKeys, string> = {
  large: '2.8rem',
  medium: '2rem',
  small: '1.6rem',
}

const getInitials = (name: string) => {
  const words = name.trim().split(/\s+/).filter(Boolean)

  if (words.length === 0) return ''
  if (words.length === 1) return words[0].charAt(0).toUpperCase()

  return `${words[0].charAt(0)}${words[words.length - 1].charAt(0)}`.toUpperCase()
}

export const Avatar = ({
  // NOTE: `src` is undefined intentionally to simply control when only `Icon` is rendered
  src = undefined,
  alt = 'Avatar',
  size = 'medium',
  shape = 'circle',
  fallback,
  fallbackIcon = UserIcon,
  fallbackIconColor,
  forceShowIcon,
  zoomable = false,
  ...rest
}: AvatarProps) => {
  const { hasError, onError } = useImageFallback(src)

  const initials = getInitials(alt)
  const zoomProps =
    zoomable && !hasError ? getImagePreviewTriggerProps({ alt, src, title: alt }) : {}

  return (
    <S.Avatar
      {...rest}
      {...zoomProps}
      style={{ ...rest.style, ...zoomProps.style }}
      $shape={shape}
      $size={size}
      data-component-id="Avatar"
    >
      {!hasError ? (
        <img src={src} alt={alt} onError={onError} />
      ) : initials && !forceShowIcon ? (
        <S.Initials>{initials}</S.Initials>
      ) : (
        (fallback ?? <Icon color={fallbackIconColor} icon={fallbackIcon} />)
      )}
      {zoomProps.onClick && <ZoomHint size={ZOOM_HINT_SIZES[size]} />}
    </S.Avatar>
  )
}
